import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from './api'

export type Me = {
  user: { id: string; email: string } | null
  is_staff: boolean
  staff_role: 'approver' | 'admin' | null
  // CHANGE-042: logo_url for the switcher; a partner's status (Profile shows once set-up is sent)
  companies: { id: string; name: string; role: 'admin' | 'member'; logo_url?: string | null }[]
  partners: { id: string; name: string; slug: string; role: 'owner' | 'admin' | 'viewer'; logo_url?: string | null; status?: string }[]
  // CHANGE-024: a staff member viewing as a company or partner (read-only)
  view_as?: { kind: 'company' | 'partner'; id: string; name: string } | null
  // Switches the server sets (CHANGE-travel: TRAVEL_ENABLED; CHANGE-card: CARD_APPLICATIONS_ENABLED)
  features?: { travel?: boolean; card?: boolean; card_only_app?: boolean; microsoft_login?: boolean }   // card_only_app: CARD_ONLY_APP
  // Where people write with questions (CHANGE-034: SUPPORT_EMAIL)
  support_email?: string
  // CHANGE-card: came only for the card (their company was made by the card flow): the app shows the card pages only
  card_only?: boolean
}

export type CompanyRef = Me['companies'][number]
export type PartnerRef = Me['partners'][number]

const SIGNED_OUT: Me = { user: null, is_staff: false, staff_role: null, companies: [], partners: [] }

export const ME_KEY = ['me'] as const

async function fetchMe(): Promise<Me> {
  try {
    const me = await api<Me>('GET', '/api/me')
    return { ...SIGNED_OUT, ...me, companies: me.companies ?? [], partners: me.partners ?? [] }
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return SIGNED_OUT
    throw e
  }
}

/** `poll` (ms) re-reads /api/me on a timer, for pages waiting on something else (e.g. the founder's agent) to change it. */
export function useMe(poll?: number): { me: Me | undefined; isLoading: boolean; failed: boolean } {
  // Re-read on focus too: a coding agent may create or join a company from outside this tab.
  const q = useQuery({ queryKey: ME_KEY, queryFn: fetchMe, staleTime: 30_000, refetchOnWindowFocus: true, refetchInterval: poll })
  return { me: q.data, isLoading: q.isLoading, failed: q.isError || q.failureCount > 0 }
}

/** Call after sign-in, sign-out or joining a company so the shell re-reads /api/me. */
export function useRefreshMe(): () => Promise<void> {
  const qc = useQueryClient()
  return useCallback(() => qc.invalidateQueries({ queryKey: ME_KEY }), [qc])
}

// Current company / partner: the one the user picked in the switcher (kept in localStorage),
// else the first one /api/me lists.

const COMPANY_KEY = 'pr_company_id'
const PARTNER_KEY = 'pr_partner_id'

function load(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function save(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // storage blocked: selection lasts for this page only
  }
}

type Selection = {
  companyId: string | null
  partnerId: string | null
  setCompanyId: (id: string) => void
  setPartnerId: (id: string) => void
}

const SelectionContext = createContext<Selection | null>(null)

export function SelectionProvider({ children }: { children: ReactNode }) {
  const [companyId, setC] = useState<string | null>(() => load(COMPANY_KEY))
  const [partnerId, setP] = useState<string | null>(() => load(PARTNER_KEY))
  const value = useMemo<Selection>(
    () => ({
      companyId,
      partnerId,
      setCompanyId: (id) => {
        save(COMPANY_KEY, id)
        setC(id)
      },
      setPartnerId: (id) => {
        save(PARTNER_KEY, id)
        setP(id)
      },
    }),
    [companyId, partnerId],
  )
  return <SelectionContext.Provider value={value}>{children}</SelectionContext.Provider>
}

function useSelection(): Selection {
  const s = useContext(SelectionContext)
  if (!s) throw new Error('SelectionProvider missing')
  return s
}

export function useCurrentCompany(): CompanyRef | undefined {
  const { me } = useMe()
  const { companyId } = useSelection()
  const list = me?.companies ?? []
  return list.find((c) => c.id === companyId) ?? list[0]
}

export function useCurrentPartner(): PartnerRef | undefined {
  const { me } = useMe()
  const { partnerId } = useSelection()
  const list = me?.partners ?? []
  return list.find((p) => p.id === partnerId) ?? list[0]
}

/** Setters for the area switchers; features rarely need these. */
export function useSelectCompany(): (id: string) => void {
  return useSelection().setCompanyId
}

export function useSelectPartner(): (id: string) => void {
  return useSelection().setPartnerId
}
